'use client';

import type React from 'react';

import MovieReleaseBadge from '@/app/[locale]/(default-layout)/program/_components/MovieReleaseBadge';

import { HrefOrHrefWithParams } from 'next-intl/dist/types/navigation/shared/utils';

import { getAgeRatingIcon } from '@/helpers/movie-helper';
import { useAgeRatingText } from '@cineamo/ui/src/helpers/movie-helper';

import { CachedContentData } from '@/hooks/useCachedContentData';
import useSharePageLink from '@/hooks/useSharePageLink';

import { IconShareBox24 } from '@cineamo/ui/src/icons/ic-share-box';

import SharedContentTitle from '@cineamo/composables/src/content/ContentTitle';

import { Link } from '@/i18n/routing';
import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

/**
 * Cinema-website adapter for the shared `ContentTitle`: maps `CachedContentData` onto the shared
 * layout and supplies the app-specific pieces (typed `Link`, domain-configuration theming, the
 * FSK/GRETA badges with their info modals and tracking, the share action).
 */
const ContentTitle: React.FC<{
    content?: CachedContentData;
    showMovieDuration?: boolean;
    showGenres?: boolean;
    /** Full release date instead of the bare year — detail page only, see the shared `ContentTitle`. */
    showReleaseDate?: boolean;
    href?: HrefOrHrefWithParams<'/program/[contentId]'>;
    /** Renders the title as a semantic `<h1>` instead of a `div`. Only takes effect when no `href` is given
     * (link rows must stay non-heading elements) — pass this only from the single canonical page usage of
     * a title, never from list rows. */
    as?: 'h1' | 'heading1';
    /** Share button in the title row. Defaults to on; the detail page renders its own. */
    showShare?: boolean;
    /** "New release" badge next to the title. Defaults to on. */
    showReleaseBadge?: boolean;
    /** Replace the shared title / meta-label typography. */
    classNameTitle?: string;
    classNameMetaLabel?: string;
    /** Replace the age-rating badge's icon classes (size). */
    classNameAgeRating?: string;
}> = ({
    showGenres,
    showMovieDuration,
    showReleaseDate,
    href,
    as,
    showShare = true,
    showReleaseBadge = true,
    classNameTitle,
    classNameMetaLabel,
    classNameAgeRating,
    ...props
}) => {
    const domainConfiguration = useDomainConfiguration();
    const content = props.content;
    const countryCode = domainConfiguration.cinema.countryCode;

    const ageRatingText = useAgeRatingText(content?.ageRating, countryCode);
    const ageRatingElement = getAgeRatingIcon(content?.ageRating, countryCode, {
        movieTitle: content?.title,
        movieId: content?.movieId,
        classNameIcon: classNameAgeRating
    });

    const sharePageLink = useSharePageLink(content);

    return (
        <SharedContentTitle
            title={content?.title ?? ''}
            renderTitle={({ className, children }) => {
                if (href) {
                    return (
                        <Link
                            href={href}
                            aria-label={content?.title}
                            className={className}>
                            {children}
                        </Link>
                    );
                }
                // 'heading1' = level-1 heading semantics without a second `<h1>` tag, for the twin
                // (responsive) instance of a title whose sibling already renders the real `<h1>`.
                const TitleElement = as === 'h1' ? 'h1' : 'div';
                return (
                    <TitleElement
                        aria-label={content?.title}
                        {...(as === 'heading1' && { role: 'heading', 'aria-level': 1 })}
                        className={className}>
                        {children}
                    </TitleElement>
                );
            }}
            // CachedContentData already carries the movie's release year, running time, genres and
            // GRETA payload, resolved in useCachedContentData
            movie={content}
            showMovieDuration={showMovieDuration}
            showGenres={showGenres}
            showReleaseDate={showReleaseDate}
            ageRatingBadge={ageRatingElement}
            ageRatingText={ageRatingText}
            gretaIconVariant={domainConfiguration.websiteConfiguration?.gretaIconVariant}
            titleBadges={
                /*Show release badge if it is movie and movie has release date*/
                showReleaseBadge &&
                content?.hasMovie &&
                content?.releaseDate && <MovieReleaseBadge movieReleaseDate={content?.releaseDate} />
            }
            classNameTitle={classNameTitle}
            classNameMetaLabel={classNameMetaLabel}
            titleActions={
                showShare && (
                    <button
                        onClick={sharePageLink}
                        className="relative z-10 flex shrink-0 hover:opacity-80 pointer-events-auto cursor-pointer transition-opacity duration-150">
                        <IconShareBox24 className="w-20 h-20" />
                    </button>
                )
            }
            styleMeta={{
                color: domainConfiguration.style.onBackgroundColor,
                opacity: 0.8
            }}
        />
    );
};

export default ContentTitle;
